import type { ReactNode } from "react";
import { Link } from "wouter";
import { PaginaPublica } from "@/components/landing/layout-publico";
import { CelularAviso, CelularConversa, CelularVenda } from "@/components/landing/celulares";
import {
  CORRETOR,
  GRATUITO,
  HERO,
  JORNADA,
  MODULOS,
  NUMEROS_DE_USO,
  PERGUNTAS,
  PROVA,
  WHATSAPP,
  linkContatoCorretor,
  type LadoDoPasso,
} from "@/components/landing/conteudo";

/**
 * Landing pública de rizoma.farm — lançamento Cacho + Mercado, gratuito.
 *
 * Só monta: TODO o texto está em `components/landing/conteudo.ts` (e os números
 * de uso, que precisam ser atualizados antes de publicar, também). Sem preço, sem
 * checkout, sem print de tela e sem nome real — ver as regras no topo daquele arquivo.
 *
 * Cores pelos tokens `landing-*` (index.css) e `primary`; a guarda de cor do CI
 * vale aqui como em qualquer página. Mobile-first: conferir em 375 px sem
 * rolagem lateral.
 */

function IconeCheck({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className={`h-4 w-4 shrink-0 mt-0.5 ${className}`}>
      <circle cx="8" cy="8" r="8" fill="currentColor" />
      <path d="M5 8.5l2 2 4-4" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Seta() {
  return (
    <span aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
      →
    </span>
  );
}

function TituloSecao({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={`font-titulo font-extrabold text-3xl md:text-4xl tracking-tight leading-tight ${className}`}>
      {children}
    </h2>
  );
}

/** O chip do LADO do passo: verde do app para o produtor, lilás para o comprador. */
function ChipLado({ lado }: { lado: LadoDoPasso }) {
  const cor = lado === "Produtor" ? "bg-primary text-primary-foreground" : "bg-landing-lilas text-white";
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${cor}`}>{lado}</span>
  );
}

function Hero() {
  return (
    <section className="px-4 pt-10 pb-14 md:pt-16 lg:pb-20" data-testid="secao-hero">
      <div className="max-w-6xl mx-auto grid xl:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-landing-folha">{HERO.eyebrow}</p>
          <h1 className="font-titulo font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-3">
            {HERO.tituloInicio} <span className="text-landing-lilas">{HERO.tituloDestaque}</span>
          </h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">{HERO.subtitulo}</p>

          <div className="mt-7 grid sm:grid-cols-2 gap-3">
            {HERO.caminhos.map((c) => {
              const conteudo = (
                <>
                  <span className="flex items-center justify-between gap-2 font-titulo font-semibold text-lg">
                    {c.titulo} <Seta />
                  </span>
                  <span className="block mt-1 text-sm text-muted-foreground leading-snug">{c.texto}</span>
                </>
              );
              const classe =
                "group block rounded-2xl border border-border bg-background p-4 shadow-sm hover:border-primary hover:shadow-md transition";
              return c.interno ? (
                <Link key={c.id} href={c.href} className={classe} data-testid={`caminho-${c.id}`}>
                  {conteudo}
                </Link>
              ) : (
                <a key={c.id} href={c.href} className={classe} data-testid={`caminho-${c.id}`}>
                  {conteudo}
                </a>
              );
            })}
          </div>

          <p className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
            <IconeCheck className="text-primary" />
            {HERO.gratuito}
          </p>
        </div>

        {/* Abaixo de 600 px fica só o aviso: dois celulares lado a lado não cabem
            em 375 px sem rolagem lateral. Lado a lado SEM sobrepor — sobreposto, o
            aviso cobria a coluna de valores da venda. Por isso as duas colunas do
            hero só começam no xl (1280 px): antes disso os celulares (2 × 240 px)
            não cabem ao lado do texto e descem para baixo dele. */}
        <div className="flex justify-center items-start gap-5">
          <CelularVenda className="hidden min-[600px]:block -rotate-2 mt-10" />
          <CelularAviso className="rotate-2" />
        </div>
      </div>
    </section>
  );
}

function FaixaProva() {
  return (
    <section className="bg-landing-folha text-white px-4 py-12" data-testid="secao-prova">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.6fr] gap-8 items-center">
        <p className="text-white/85 text-base md:text-lg leading-relaxed max-w-md">{PROVA}</p>
        <dl className="grid grid-cols-1 min-[480px]:grid-cols-3 gap-6">
          {NUMEROS_DE_USO.map((n) => (
            <div key={n.rotulo} className="flex flex-col-reverse">
              <dt className="mt-1 text-sm text-white/80">{n.rotulo}</dt>
              <dd className="font-titulo font-extrabold text-4xl md:text-5xl tracking-tight">{n.valor}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

function Modulos() {
  return (
    <section id="modulos" className="scroll-mt-20 px-4 py-16 md:py-20" data-testid="secao-modulos">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <TituloSecao>{MODULOS.titulo}</TituloSecao>
          <p className="mt-3 text-muted-foreground text-lg">{MODULOS.subtitulo}</p>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {MODULOS.cartoes.map((m) => (
            <article
              key={m.id}
              className="rounded-3xl border border-border bg-background p-6 md:p-8 shadow-sm"
              data-testid={`modulo-${m.id}`}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{m.resumo}</span>
              <h3 className="font-titulo font-extrabold text-2xl md:text-3xl tracking-tight text-landing-folha mt-1">
                {m.nome}
              </h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{m.texto}</p>
              <ul className="mt-5 space-y-2.5">
                {m.itens.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm">
                    <IconeCheck className="text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">{MODULOS.convite}</p>
      </div>
    </section>
  );
}

function Jornada() {
  return (
    <section id="jornada" className="scroll-mt-20 px-4 py-16 md:py-20 bg-background" data-testid="secao-jornada">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <TituloSecao>{JORNADA.titulo}</TituloSecao>
          <p className="mt-3 text-muted-foreground text-lg">{JORNADA.subtitulo}</p>
        </div>

        {/* Celular: lista vertical com a linha à esquerda. Desktop (lg): cinco
            colunas com a linha horizontal passando pelos números. */}
        <div className="relative mt-10">
          <div aria-hidden="true" className="absolute left-5 top-3 bottom-3 w-px bg-border lg:hidden" />
          <div aria-hidden="true" className="hidden lg:block absolute top-5 left-[10%] right-[10%] h-px bg-border" />
          <ol className="relative space-y-7 lg:space-y-0 lg:grid lg:grid-cols-5 lg:gap-6">
          {JORNADA.passos.map((p, i) => (
            <li key={p.titulo} className="relative flex gap-4 lg:flex-col lg:items-center lg:text-center lg:gap-3">
              <span
                className={`relative z-10 h-10 w-10 shrink-0 rounded-full flex items-center justify-center font-titulo font-extrabold ring-4 ring-background ${
                  p.lado === "Produtor" ? "bg-primary text-primary-foreground" : "bg-landing-lilas text-white"
                }`}
              >
                {i + 1}
              </span>
              <div className="pt-1 lg:pt-0">
                <ChipLado lado={p.lado} />
                <h3 className="font-titulo font-semibold text-lg mt-1.5">{p.titulo}</h3>
                <p className="text-sm text-muted-foreground leading-snug mt-1">{p.texto}</p>
              </div>
            </li>
          ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function NoWhatsApp() {
  return (
    <section className="px-4 py-16 md:py-20" data-testid="secao-whatsapp">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <TituloSecao>{WHATSAPP.titulo}</TituloSecao>
          <p className="mt-3 text-muted-foreground text-lg">{WHATSAPP.subtitulo}</p>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-8">
          {WHATSAPP.conversas.map((c) => (
            <figure key={c.id} className="flex flex-col items-center gap-3" data-testid={`conversa-${c.id}`}>
              <figcaption className="text-sm font-semibold text-muted-foreground">{c.para}</figcaption>
              <CelularConversa linhas={c.linhas} />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function ParaCorretores() {
  // Sem número configurado o link é `#perguntas` — nunca um número inventado.
  const contato = linkContatoCorretor(import.meta.env.VITE_CONTATO_WHATSAPP as string | undefined);
  const externo = contato.indexOf("https://") === 0;
  return (
    <section id="corretor" className="scroll-mt-20 px-4 py-16 md:py-20 bg-landing-lilas-suave" data-testid="secao-corretor">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
        <div>
          <span className="inline-block rounded-full bg-landing-lilas text-white text-xs font-semibold uppercase tracking-wide px-3 py-1">
            {CORRETOR.eyebrow}
          </span>
          <TituloSecao className="mt-4">{CORRETOR.titulo}</TituloSecao>
          <ul className="mt-7 space-y-3.5">
            {CORRETOR.beneficios.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <IconeCheck className="text-landing-lilas" />
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              href="/login"
              className="inline-flex justify-center items-center rounded-xl bg-landing-lilas text-white font-semibold px-5 py-3 hover:bg-landing-lilas/90 transition-colors"
              data-testid="corretor-entrar"
            >
              {CORRETOR.entrar}
            </Link>
            <a
              href={contato}
              {...(externo ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="inline-flex justify-center items-center rounded-xl border border-landing-lilas bg-background text-foreground font-semibold px-5 py-3 hover:bg-background/70 transition-colors"
              data-testid="corretor-contato"
            >
              {CORRETOR.contato}
            </a>
          </div>
        </div>

        {/* Espaço do vídeo, 9:16. Sem player: o vídeo ainda está em produção. */}
        <div
          className="w-full max-w-[220px] lg:max-w-[260px] mx-auto aspect-[9/16] rounded-3xl bg-landing-folha text-white flex flex-col items-center justify-center gap-4 p-6 text-center shadow-xl"
          data-testid="video-corretor"
        >
          <span aria-hidden="true" className="h-14 w-14 rounded-full bg-white/15 flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="h-6 w-6 ml-0.5" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
          <div>
            <p className="font-titulo font-semibold text-lg leading-tight">{CORRETOR.video.titulo}</p>
            <p className="text-sm text-white/75 mt-1">{CORRETOR.video.estado}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Gratuito() {
  return (
    <section className="px-4 py-16 md:py-20" data-testid="secao-gratuito">
      <div className="max-w-5xl mx-auto rounded-3xl bg-landing-folha text-white p-7 md:p-12 grid md:grid-cols-[1.2fr_1fr] gap-8 items-center">
        <div>
          <TituloSecao>{GRATUITO.titulo}</TituloSecao>
          <p className="mt-4 text-white/85 leading-relaxed">{GRATUITO.texto}</p>
          <Link
            href="/signup"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-background text-landing-folha font-semibold px-5 py-3 hover:bg-background/90 transition-colors"
            data-testid="gratuito-comecar"
          >
            Começar grátis <span aria-hidden="true">→</span>
          </Link>
        </div>
        <ul className="space-y-3">
          {GRATUITO.itens.map((item) => (
            <li key={item} className="flex items-start gap-3 rounded-2xl bg-white/10 px-4 py-3">
              <IconeCheck className="text-landing-lilas" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Perguntas() {
  return (
    <section id="perguntas" className="scroll-mt-20 px-4 pb-20" data-testid="secao-perguntas">
      <div className="max-w-3xl mx-auto">
        <TituloSecao className="text-center">Perguntas frequentes</TituloSecao>
        <div className="mt-10 space-y-3">
          {PERGUNTAS.map((p) => (
            <details
              key={p.pergunta}
              open={p.aberta}
              className="group rounded-2xl border border-border bg-background overflow-hidden"
            >
              <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer font-semibold list-none hover:bg-foreground/[0.03] transition-colors">
                {p.pergunta}
                <span aria-hidden="true" className="shrink-0 text-muted-foreground transition-transform group-open:rotate-180">
                  ▾
                </span>
              </summary>
              <p className="px-5 pb-5 -mt-1 text-muted-foreground leading-relaxed">{p.resposta}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  return (
    <PaginaPublica naLanding>
      <Hero />
      <FaixaProva />
      <Modulos />
      <Jornada />
      <NoWhatsApp />
      <ParaCorretores />
      <Gratuito />
      <Perguntas />
    </PaginaPublica>
  );
}
